/**
 * Accounts (D37): your own on the Settings page, everyone's on the Users page.
 *
 * The Users page is shown only to administrators, but hiding it is courtesy,
 * not protection: the server refuses every account route to anyone else.
 * There is no self-registration or email invitation. An administrator creates
 * the account and hands over its first password, which is simply the password:
 * nobody is made to change it.
 */
import { useEffect, useId, useState, type FormEvent } from "react";
import { api, type Account } from "./api/client";
import { useAuth } from "./auth";
import { ConfirmDialog, Empty, ErrorNotice, Loading, RelativeTime } from "./components";
import { AddIcon, EditIcon } from "./icons";

const MINIMUM_PASSWORD_LENGTH = 12;

// --- Your own account -----------------------------------------------------------

export function AccountSettings() {
  const { user, setUser, signedOut } = useAuth();
  const [name, setName] = useState(user?.display_name ?? "");
  const [nameError, setNameError] = useState<unknown>(null);
  const [nameSaved, setNameSaved] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [passwordError, setPasswordError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [elsewhere, setElsewhere] = useState<string | null>(null);

  useEffect(() => setName(user?.display_name ?? ""), [user?.display_name]);
  if (!user) return null;

  const saveName = async (event: FormEvent) => {
    event.preventDefault();
    setNameError(null);
    setNameSaved(false);
    try {
      setUser(await api.updateProfile(name));
      setNameSaved(true);
    } catch (caught) {
      setNameError(caught);
    }
  };

  const mismatch = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && next.length < MINIMUM_PASSWORD_LENGTH;
  const canChange = current.length > 0 && next.length >= MINIMUM_PASSWORD_LENGTH && confirm === next && !busy;

  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (!canChange) return;
    setBusy(true);
    setPasswordError(null);
    try {
      await api.changePassword(current, next);
      // Every session has ended, this one included: sign in with the new password.
      signedOut();
    } catch (caught) {
      setPasswordError(caught);
      setBusy(false);
    }
  };

  const signOutElsewhere = async () => {
    const { sessions_revoked } = await api.signOutElsewhere();
    setElsewhere(
      sessions_revoked === 0
        ? "You were not signed in anywhere else."
        : `Signed out of ${sessions_revoked} other session${sessions_revoked === 1 ? "" : "s"}.`,
    );
  };

  return (
    <section className="card stack" aria-label="Account">
      <h3 style={{ margin: 0 }}>Account</h3>
      <p className="muted" style={{ margin: 0 }}>
        Signed in as {user.email}
        {user.is_admin ? " · administrator" : ""}
      </p>

      <form className="row" onSubmit={(event) => void saveName(event)}>
        <label className="row">
          Display name
          <input
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
              setNameSaved(false);
            }}
          />
        </label>
        <button type="submit" disabled={name.trim() === "" || name.trim() === user.display_name}>
          Save
        </button>
        {nameSaved && (
          <span className="muted" role="status">
            Saved.
          </span>
        )}
      </form>
      <ErrorNotice error={nameError} />

      <form className="stack account-password" onSubmit={(event) => void changePassword(event)} aria-label="Change password">
        <h4 style={{ margin: 0 }}>Change password</h4>
        <label className="row">
          Current password
          <input type="password" autoComplete="current-password" value={current} onChange={(event) => setCurrent(event.target.value)} />
        </label>
        <label className="row">
          New password
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            aria-invalid={tooShort || undefined}
            aria-describedby="new-password-help"
            onChange={(event) => setNext(event.target.value)}
          />
        </label>
        <label className="row">
          Confirm new password
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            aria-invalid={mismatch || undefined}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </label>
        <p id="new-password-help" className="muted" style={{ margin: 0 }}>
          {mismatch
            ? "The two new passwords differ."
            : `At least ${MINIMUM_PASSWORD_LENGTH} characters. Changing it signs you out everywhere, here included.`}
        </p>
        <div className="row">
          <button type="submit" className="primary" disabled={!canChange}>
            {busy ? "Changing…" : "Change password"}
          </button>
        </div>
        <ErrorNotice error={passwordError} />
      </form>

      <div className="row">
        <button type="button" onClick={() => void signOutElsewhere()}>
          Sign out everywhere else
        </button>
        {elsewhere && (
          <span className="muted" role="status">
            {elsewhere}
          </span>
        )}
      </div>
    </section>
  );
}

// --- Everyone's, for administrators ---------------------------------------------

/** Two letters for an account's avatar: initials of the name, else of the email. */
function initials(account: Account): string {
  const words = account.display_name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words[0]![0]! + words[words.length - 1]![0]! : (words[0] ?? account.email).slice(0, 2);
  return letters.toUpperCase();
}

export function UsersPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = () =>
    api
      .listAccounts()
      .then((list) => {
        setAccounts(list);
        setLoadError(null);
      })
      .catch(setLoadError);
  useEffect(() => void reload(), []);

  // The whole page is administrators' (D37): the server refuses the list to
  // anyone else, so nobody but an administrator ever sees an edit control.
  if (!user?.is_admin) {
    return (
      <div className="stack">
        <h2>Users</h2>
        <Empty>Only an administrator can manage accounts.</Empty>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="page-header">
        <h2>Users</h2>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Everyone who can sign in. Access to programmes and projects is given on each one, by its owners.
      </p>
      {notice && (
        <p className="muted" role="status" style={{ margin: 0 }}>
          {notice}
        </p>
      )}
      <ErrorNotice error={loadError} />
      {accounts === null && !loadError ? (
        <Loading what="accounts" />
      ) : (
        accounts && (
          <div className="data-table">
            <table aria-label="Accounts">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last sign-in</th>
                  <th className="numeric">Designs</th>
                  <th>
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const self = account.id === user.user_id;
                  return (
                    <tr key={account.id} className={account.is_active ? undefined : "inactive"}>
                      <td>
                        <div className="person">
                          <span className="avatar" aria-hidden="true">
                            {initials(account)}
                          </span>
                          <div className="person-text">
                            <span className="person-name">
                              {account.display_name}
                              {self && <span className="muted"> (you)</span>}
                            </span>
                            <span className="person-email">{account.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge${account.is_admin ? " badge-accent" : ""}`}>
                          {account.is_admin ? "Administrator" : "Member"}
                        </span>
                      </td>
                      <td>
                        <span className={`status-dot${account.is_active ? " active" : ""}`} aria-hidden="true" />
                        {account.is_active ? "Active" : "Deactivated"}
                        {account.is_active && account.live_sessions > 0 && (
                          <span className="muted"> · signed in ({account.live_sessions})</span>
                        )}
                      </td>
                      <td>
                        {account.last_login_at ? <RelativeTime value={account.last_login_at} /> : <span className="muted">Never</span>}
                      </td>
                      <td className="numeric">{account.design_count ?? 0}</td>
                      <td className="row-actions">
                        <button
                          type="button"
                          className="icon-button neutral"
                          aria-label={`Edit ${account.email}`}
                          title={`Edit ${account.display_name}`}
                          onClick={() => setEditing(account)}
                        >
                          <EditIcon size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="data-table-footer">
              <button type="button" className="add-row" aria-label="Add a user" onClick={() => setCreating(true)}>
                <AddIcon size={16} />
                <span aria-hidden="true">Add user</span>
              </button>
            </div>
          </div>
        )
      )}

      {creating && (
        <CreateAccountDialog
          onCreated={async (account) => {
            setCreating(false);
            setNotice(`Added ${account.email}. Give them their password directly; they can change it in Settings.`);
            await reload();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {editing && (
        <EditAccountDialog
          account={editing}
          self={editing.id === user.user_id}
          onSaved={async (message) => {
            setEditing(null);
            setNotice(message);
            await reload();
          }}
          onCancel={() => {
            setEditing(null);
            // Part of a save may have landed before a refusal; show what did.
            void reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Everything an administrator can change on an account, saved together. Each
 * change is still its own request and its own activity entry; the server
 * refuses what would leave the deployment unmanageable, and the refusal is
 * shown here.
 */
function EditAccountDialog({
  account,
  self,
  onSaved,
  onCancel,
}: {
  account: Account;
  self: boolean;
  onSaved: (message: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [admin, setAdmin] = useState(account.is_admin);
  const [active, setActive] = useState(account.is_active);
  const [password, setPassword] = useState("");
  const [signOut, setSignOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const passwordId = useId();

  const passwordValid = password === "" || password.length >= MINIMUM_PASSWORD_LENGTH;
  // Deactivating or a new password signs them out anyway.
  const canSignOut = account.is_active && active && account.live_sessions > 0 && password === "";
  const changed = admin !== account.is_admin || active !== account.is_active || password !== "" || (canSignOut && signOut);

  const save = async () => {
    setBusy(true);
    setError(null);
    const done: string[] = [];
    try {
      const change: { is_active?: boolean; is_admin?: boolean } = {};
      if (active !== account.is_active) change.is_active = active;
      if (admin !== account.is_admin) change.is_admin = admin;
      if (Object.keys(change).length > 0) {
        await api.updateAccount(account.id, change);
        if (change.is_active !== undefined) done.push(active ? "reactivated" : "deactivated");
        if (change.is_admin !== undefined) done.push(admin ? "made an administrator" : "no longer an administrator");
      }
      if (password !== "") {
        await api.resetPassword(account.id, password);
        done.push("given a new password");
      }
      if (canSignOut && signOut) {
        await api.revokeAccountSessions(account.id);
        done.push("signed out everywhere");
      }
      await onSaved(`${account.email}: ${done.join(", ")}.`);
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      title={`Edit ${account.display_name}`}
      confirmLabel="Save changes"
      busyLabel="Saving…"
      danger={false}
      busy={busy}
      focusField
      confirmDisabled={!changed || !passwordValid}
      error={error}
      onConfirm={() => void save()}
      onCancel={onCancel}
    >
      <p className="muted" style={{ margin: 0 }}>
        {account.email}
      </p>
      <fieldset className="stack field-group">
        <legend>Role</legend>
        <label className="row">
          <input type="radio" name="role" checked={!admin} disabled={self} onChange={() => setAdmin(false)} />
          Member
        </label>
        <label className="row">
          <input type="radio" name="role" checked={admin} disabled={self} onChange={() => setAdmin(true)} />
          Administrator
        </label>
        <span className="muted field-help">
          {self
            ? "You cannot remove your own administrator access."
            : "Administrators create, deactivate and reset every account. It gives no access to projects."}
        </span>
      </fieldset>
      <fieldset className="stack field-group">
        <legend>Access</legend>
        <label className="row">
          <input type="checkbox" checked={active} disabled={self} onChange={(event) => setActive(event.target.checked)} />
          Can sign in
        </label>
        <span className="muted field-help">
          {self
            ? "You cannot deactivate your own account."
            : "Deactivating signs them out everywhere. Their work and history stay."}
        </span>
      </fieldset>
      <div className="stack field-group">
        <label htmlFor={passwordId}>New password</label>
        <input
          id={passwordId}
          type="password"
          autoComplete="new-password"
          value={password}
          aria-describedby="edit-password-help"
          onChange={(event) => setPassword(event.target.value)}
        />
        <span id="edit-password-help" className="muted field-help">
          Leave blank to keep theirs. At least {MINIMUM_PASSWORD_LENGTH} characters; it signs them out everywhere.
        </span>
      </div>
      {canSignOut && (
        <label className="row">
          <input type="checkbox" checked={signOut} onChange={(event) => setSignOut(event.target.checked)} />
          Sign out of {account.live_sessions === 1 ? "their session" : `all ${account.live_sessions} sessions`} now
        </label>
      )}
    </ConfirmDialog>
  );
}

function CreateAccountDialog({
  onCreated,
  onCancel,
}: {
  onCreated: (account: Account) => Promise<void>;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [admin, setAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const valid = email.includes("@") && name.trim() !== "" && password.length >= MINIMUM_PASSWORD_LENGTH;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      await onCreated(await api.createAccount({ email, display_name: name, password, is_admin: admin }));
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      title="Add a user"
      confirmLabel="Add user"
      busyLabel="Adding…"
      danger={false}
      busy={busy}
      focusField
      confirmDisabled={!valid}
      error={error}
      onConfirm={() => void create()}
      onCancel={onCancel}
    >
      <label className="stack">
        Email
        <input type="email" autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} />
      </label>
      <label className="stack">
        Name
        <input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} />
      </label>
      <label className="stack">
        Password
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          aria-describedby="new-account-password-help"
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <span id="new-account-password-help" className="muted">
        At least {MINIMUM_PASSWORD_LENGTH} characters. Give it to them directly.
      </span>
      <label className="row">
        <input type="checkbox" checked={admin} onChange={(event) => setAdmin(event.target.checked)} />
        Administrator: can manage accounts
      </label>
    </ConfirmDialog>
  );
}
